import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DemoModal({ onClose }) {
  const { t, lang } = useLanguage();
  const m = t.modal;
  const [form, setForm] = useState({ name: '', business: '', city: '', type: '', locations: '', phone: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/submit-demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Error en el servidor');
      setSubmitted(true);
    } catch (err) {
      setError(lang === 'es'
        ? 'Hubo un problema al enviar tu solicitud. Por favor intenta de nuevo.'
        : 'There was a problem sending your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        data-testid="demo-modal-overlay">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <motion.div className="relative z-10 bg-[#1A222B] border border-[#0F6B49]/40 rounded-2xl shadow-2xl w-full max-w-lg
          max-h-[90vh] overflow-y-auto"
          initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.3 }} data-testid="demo-modal">
          {/* Header */}
          <div className="flex items-start justify-between p-6 pb-0">
            <div>
              <h2 className="heading-md text-[#F3F1EB]" data-testid="modal-title">{m.title}</h2>
              <p className="text-[#8A9099] text-sm mt-1">{m.subtitle}</p>
            </div>
            <button onClick={onClose} className="text-[#8A9099] hover:text-[#F3F1EB] transition-colors p-1 ml-4 flex-shrink-0"
              data-testid="modal-close-btn" aria-label={m.close}>
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="p-6">
            {submitted ? (
              <motion.div className="text-center py-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                data-testid="modal-success">
                <CheckCircle size={48} className="text-[#0F6B49] mx-auto mb-4" />
                <p className="text-[#F3F1EB] font-medium text-base leading-relaxed">{m.success}</p>
                <button onClick={onClose} className="btn-primary mt-6">{m.close}</button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-form">
                {[
                  { name: 'name', label: m.fields.name, required: true },
                  { name: 'business', label: m.fields.business, required: true },
                  { name: 'city', label: m.fields.city, required: true },
                ].map(f => (
                  <div key={f.name}>
                    <label className="block text-[#8A9099] text-xs font-semibold mb-1.5 uppercase tracking-wider">{f.label}</label>
                    <input name={f.name} value={form[f.name]} onChange={handleChange} required={f.required}
                      className="w-full bg-[#1F2833] border border-[#0F6B49]/30 rounded-xl px-4 py-2.5 text-[#F3F1EB] text-sm
                        focus:outline-none focus:border-[#D5A63B]/60 focus:ring-1 focus:ring-[#D5A63B]/30 transition-colors"
                      data-testid={`modal-field-${f.name}`} />
                  </div>
                ))}

                <div>
                  <label className="block text-[#8A9099] text-xs font-semibold mb-1.5 uppercase tracking-wider">{m.fields.type}</label>
                  <select name="type" value={form.type} onChange={handleChange} required
                    className="w-full bg-[#1F2833] border border-[#0F6B49]/30 rounded-xl px-4 py-2.5 text-[#F3F1EB] text-sm
                      focus:outline-none focus:border-[#D5A63B]/60 transition-colors"
                    data-testid="modal-field-type">
                    <option value="">—</option>
                    {m.types.map(tp => <option key={tp} value={tp}>{tp}</option>)}
                  </select>
                </div>

                {[
                  { name: 'locations', label: m.fields.locations },
                  { name: 'phone', label: m.fields.phone },
                  { name: 'email', label: m.fields.email, type: 'email', required: true },
                ].map(f => (
                  <div key={f.name}>
                    <label className="block text-[#8A9099] text-xs font-semibold mb-1.5 uppercase tracking-wider">{f.label}</label>
                    <input name={f.name} type={f.type || 'text'} value={form[f.name]} onChange={handleChange} required={f.required}
                      className="w-full bg-[#1F2833] border border-[#0F6B49]/30 rounded-xl px-4 py-2.5 text-[#F3F1EB] text-sm
                        focus:outline-none focus:border-[#D5A63B]/60 focus:ring-1 focus:ring-[#D5A63B]/30 transition-colors"
                      data-testid={`modal-field-${f.name}`} />
                  </div>
                ))}

                <div>
                  <label className="block text-[#8A9099] text-xs font-semibold mb-1.5 uppercase tracking-wider">{m.fields.message}</label>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={3}
                    className="w-full bg-[#1F2833] border border-[#0F6B49]/30 rounded-xl px-4 py-2.5 text-[#F3F1EB] text-sm
                      focus:outline-none focus:border-[#D5A63B]/60 focus:ring-1 focus:ring-[#D5A63B]/30 transition-colors resize-none"
                    data-testid="modal-field-message" />
                </div>

                <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2 py-3.5"
                  data-testid="modal-submit-btn" disabled={loading}>
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                      {lang === 'es' ? 'Enviando...' : 'Sending...'}
                    </span>
                  ) : (
                    <><Send size={16} />{m.cta}</>
                  )}
                </button>
                {error && (
                  <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-xl p-3 mt-2">
                    <AlertCircle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
